package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import dev.lingvo.shared.validateEmail
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.AuthProviders
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.api.rememberSession
import dev.lingvo.site.components.marketing.AuthField
import dev.lingvo.site.components.marketing.AuthHandoff
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.components.marketing.GoogleSignInButton
import dev.lingvo.site.components.marketing.PublicTextLink
import dev.lingvo.site.pages.auth.invitationAuthDestinationFromSearch
import dev.lingvo.site.pages.auth.invitationAuthFormPath
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LDivider
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Form
import dev.lingvo.site.ds.rememberSettledWait
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

private enum class LoginAction {
    LOGIN,
}

@Page
@Composable
fun LoginPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val session = rememberSession()
    val destination = invitationAuthDestinationFromSearch(window.location.search)

    LaunchedEffect(Unit) {
        document.title = tr("login.log_in_lingvo", "Log in — Lingvo")
    }
    LaunchedEffect(session, destination) {
        if (session is SessionState.Authenticated && window.location.pathname == "/login") {
            ctx.router.tryRoutingTo(destination, UpdateHistoryMode.REPLACE)
        }
    }

    // Decide before painting — see the same guard in `SignupPage`. Showing a log-in form to someone
    // whose session is still resolving is the one screen in the product that asks a question it
    // already knows the answer to.
    // Painted only once the wait has outlasted the grace period. A cached session resolves in
    // a frame or two, and this splash rotates a wordmark — appearing and leaving inside 80ms
    // reads as a flicker, not as feedback.
    if (session is SessionState.Loading || session is SessionState.Authenticated) {
        if (rememberSettledWait(active = true)) AuthHandoff(t("login.checking_your_saved_session", "Checking your saved session."))
        return
    }
    if (session is SessionState.Failure) {
        AuthHandoff(
            message = session.message,
            onRetry = { scope.launch { Session.loadMe() } },
            onUseAnotherAccount = Session::logout,
        )
        return
    }

    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var emailTouched by remember { mutableStateOf(false) }
    var passwordTouched by remember { mutableStateOf(false) }
    var submitted by remember { mutableStateOf(false) }
    var pendingAction by remember { mutableStateOf<LoginAction?>(null) }
    var apiError by remember { mutableStateOf<String?>(null) }

    val emailError = if (submitted || emailTouched) validateEmail(email.trim()) else null
    val passwordError = if ((submitted || passwordTouched) && password.isBlank()) {
        passwordRequired()
    } else {
        null
    }

    fun finishAuthentication() {
        when (val current = Session.state) {
            is SessionState.Authenticated -> {
                if (window.location.pathname == "/login") {
                    ctx.router.tryRoutingTo(invitationAuthDestinationFromSearch(window.location.search), UpdateHistoryMode.REPLACE)
                }
            }
            is SessionState.Failure -> apiError = current.message
            SessionState.Loading -> apiError = tr(
                "login.couldnt_finish_loading_your_session_retry",
                "Couldn’t finish loading your session. Retry?",
            )
            SessionState.LoggedOut -> apiError = tr(
                "login.couldnt_verify_your_session_log_in",
                "Couldn’t verify your session. Log in again.",
            )
        }
    }

    fun submitLogin() {
        submitted = true
        apiError = null
        val currentEmailError = validateEmail(email.trim())
        val currentPasswordError = password.takeIf(String::isBlank)?.let { passwordRequired() }
        if (currentEmailError != null || currentPasswordError != null || pendingAction != null) return

        pendingAction = LoginAction.LOGIN
        scope.launch {
            try {
                Session.login(email.trim(), password)
                finishAuthentication()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: ApiException) {
                // This branch used to carry the *demo* button's message, so anyone who mistyped
                // their password was told the demo account was missing and invited to POST to
                // /dev/seed — an endpoint that truncates every table. Wrong audience, wrong advice,
                // and it advertised a destructive endpoint to whoever was least likely to want it.
                apiError = if (failure.status == 401) {
                    // The Google half of the sentence, which the server deliberately cannot say.
                    // `/auth/login` answers a Google-created account — one with no password at all
                    // — with this same 401, because naming Google in the *response* would confirm
                    // to anyone guessing an address that it belongs to a customer. Its comment
                    // relies on "the sign-in page tells people about Google instead", and the page
                    // drew the button but never said this where the failure is read. Appended for
                    // every 401 so it still confirms nothing, and only when the button it names is
                    // actually on screen — `GoogleSignInButton` draws nothing without OAuth.
                    listOfNotNull(
                        tr(
                            "login.that_email_and_password_dont_match",
                            "That email and password don't match an account.",
                        ),
                        tr(
                            "login.if_you_signed_up_with_google",
                            "If you signed up with Google, use Continue with Google below.",
                        ).takeIf { AuthProviders.googleAvailable == true },
                    ).joinToString(" ")
                } else {
                    failure.message
                }
            } catch (failure: Throwable) {
                apiError = failure.message?.takeIf(String::isNotBlank)
                    ?: tr(
                        "login.couldnt_log_in_check_your_connection",
                        "Couldn’t log in. Check your connection and retry.",
                    )
            } finally {
                pendingAction = null
            }
        }
    }

    AuthPageScaffold(
        title = t("login.welcome_back", "Welcome back"),
        subtitle = t(
            "login.return_to_the_strings_context_and",
            "Return to the strings, context, and locales already in motion.",
        ),
        alternatePrompt = t("login.new_to_lingvo", "New to Lingvo?"),
        alternateAction = t("login.create_an_account", "Create an account"),
        alternatePath = invitationAuthFormPath("/signup", destination),
    ) {
        Form(
            attrs = Modifier.fillMaxWidth().toAttrs {
                attr("novalidate", "")
                onSubmit { event ->
                    event.preventDefault()
                    submitLogin()
                }
            },
        ) {
            Column(Modifier.fillMaxWidth().gap(Space.LG)) {
                AuthField(
                    value = email,
                    onValueChange = {
                        email = it
                        apiError = null
                    },
                    label = t("login.email", "Email"),
                    placeholderText = "you@company.com",
                    inputType = InputType.Email,
                    name = "email",
                    autocomplete = "email",
                    error = emailError,
                    enabled = pendingAction == null,
                    onBlur = { emailTouched = true },
                    onCommit = ::submitLogin,
                )
                AuthField(
                    value = password,
                    onValueChange = {
                        password = it
                        apiError = null
                    },
                    label = t("login.password", "Password"),
                    placeholderText = t("login.your_password", "Your password"),
                    inputType = InputType.Password,
                    name = "password",
                    autocomplete = "current-password",
                    error = passwordError,
                    enabled = pendingAction == null,
                    onBlur = { passwordTouched = true },
                    onCommit = ::submitLogin,
                )
                // Directly under the password field, where someone who has just failed to remember
                // it is looking. The reset flow — endpoint, email, and the page the link lands on —
                // was complete except for this: nothing anywhere in the product asked for the email
                // to be sent, so a locked-out user stayed locked out.
                Row(Modifier.fillMaxWidth()) {
                    Spacer()
                    // `PublicTextLink`, not a hand-built `Link` with the same two variants. The
                    // pair it was copied from is only half of what that component is: the style
                    // carries the accent focus ring at a 2px offset and the 10px of vertical
                    // padding that makes a 40px target. Without it, the one link a locked-out
                    // reader needs was the only link on the screen with the browser's default ring
                    // and a hit area under the floor the rest of the product honours.
                    PublicTextLink(
                        "/forgot-password",
                        t("login.forgot_your_password", "Forgot your password?"),
                        Modifier.fontSize(TypeScale.Small),
                    )
                }
                if (apiError != null) {
                    LAlert(
                        title = t("login.couldnt_log_in", "Couldn’t log in"),
                        description = apiError.orEmpty(),
                        modifier = Modifier.fillMaxWidth(),
                        variant = LAlertVariant.DANGER,
                        // It appeared because the reader just pressed "Log in". `AuthField` already
                        // announces a mistyped email; without this the refusal that actually locks
                        // someone out was the one thing on the screen never read aloud.
                        live = true,
                    )
                }
                LButton(
                    label = t("login.log_in", "Log in"),
                    onClick = ::submitLogin,
                    modifier = Modifier.fillMaxWidth(),
                    enabled = pendingAction == null,
                    loading = pendingAction == LoginAction.LOGIN,
                )
                if (AuthProviders.googleAvailable != false) LDivider(label = "or")
                GoogleSignInButton(
                    redirectTo = destination,
                    enabled = pendingAction == null,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}

private fun passwordRequired(): String = tr("login.password_is_required", "Password is required.")
